import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Button, Stack, Typography, useTheme } from '@mui/material';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { getDashboard, type DashboardPayload } from '../lib/api';
import { WAF_YELLOW_DARK } from '../theme';
import { LoadingScreen } from '../components/LoadingScreen';
import { StatCard, SectionHeading } from '../components/DashboardWidgets';
import { EntryList } from './EntryList';

const primaryButtonSx = {
  backgroundColor: WAF_YELLOW_DARK,
  color: 'text.primary',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#E6B800' },
};

export function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load');
      });
    return () => { cancelled = true; };
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <LoadingScreen />;

  const t = data.totals;
  const cr = data.change_requests;
  const users = data.users;

  return (
    <Stack spacing={4}>
      <Typography variant="h3" component="h1" align="center">
        Dashboard
      </Typography>

      {/* ───────────────────────── Entrant data ──────────────────────────── */}
      <Stack spacing={2}>
        <SectionHeading title="Entrant data" />

        {/* Entry completeness. Total entries carries the "view all" CTA. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <StatCard
            icon={<InventoryOutlinedIcon />}
            label="Total entries"
            value={t.total}
            action={
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardOutlinedIcon />}
                component={RouterLink}
                to="/entries"
                sx={primaryButtonSx}
              >
                View all entries
              </Button>
            }
          />
          <StatCard icon={<PhotoOutlinedIcon />} label="Hero photo" value={t.with_hero} total={t.total} />
          <StatCard icon={<CollectionsOutlinedIcon />} label="5+ photos" value={t.with_five_plus} total={t.total} />
          <StatCard icon={<SlideshowOutlinedIcon />} label="Presentation boards" value={t.with_presentation} total={t.total} />
          <StatCard icon={<MovieOutlinedIcon />} label="Video" value={t.with_video} total={t.total} />
        </Stack>

        {/* Change-request status + contact confirmation. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <StatCard
            icon={<HourglassEmptyOutlinedIcon />}
            label="Outstanding change requests"
            value={cr.pending}
            accent={theme.palette.warning.main}
            onClick={() => navigate('/change-requests')}
          />
          <StatCard
            icon={<CheckCircleOutlinedIcon />}
            label="Approved change requests"
            value={cr.approved}
            accent={theme.palette.success.main}
            onClick={() => navigate('/change-requests')}
          />
          <StatCard
            icon={<HighlightOffOutlinedIcon />}
            label="Rejected change requests"
            value={cr.rejected}
            accent={theme.palette.error.main}
            onClick={() => navigate('/change-requests')}
          />
          <StatCard
            icon={<MarkEmailReadOutlinedIcon />}
            label="Contact details confirmed"
            value={users.contact_confirmed}
            total={users.entrants}
            onClick={() => navigate('/users')}
          />
          <StatCard
            icon={<LoginOutlinedIcon />}
            label="Users logged in"
            value={users.active}
            total={users.total}
            onClick={() => navigate('/users')}
          />
        </Stack>
      </Stack>

      {/* ───────────────── All entries (full Entries page embedded) ───────────────
          So admins can browse, filter and open entries straight from the
          dashboard. EntryList brings its own centred header, filters and grid. */}
      <EntryList />
    </Stack>
  );
}
